import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name) => name.split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase();

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((res) => setTestimonials(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 lg:gap-16 items-center">
          <div>
            <p className="animate-fade-up font-mono text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-amber-700" data-testid="hero-eyebrow">
              Adtech · Growth Marketing · AI
            </p>
            <h1
              className="animate-fade-up stagger-1 mt-6 max-w-3xl font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.15]"
              data-testid="hero-headline"
            >
              Hi, I'm Abhishek — I write about AI, growth marketing, and adtech, and build small tools to solve real problems.
            </h1>
            <p
              className="animate-fade-up stagger-2 mt-6 max-w-2xl text-base sm:text-lg text-stone-600 leading-relaxed"
              data-testid="hero-bio"
            >
              Adtech professional with 7+ years of experience across programmatic and non-programmatic advertising,
              specializing in performance and growth marketing on DSPs and affiliates. Currently leading India and SEA
              markets for Liftoff.
            </p>
            <div className="animate-fade-up stagger-3 mt-10 flex flex-wrap gap-4">
              <Link
                to="/blog"
                data-testid="hero-read-blog-button"
                className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-amber-700 hover:-translate-y-0.5"
              >
                Read the blog
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/projects"
                data-testid="hero-view-projects-button"
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-6 py-3 text-sm font-semibold text-stone-700 transition-all hover:border-stone-900 hover:text-stone-900 hover:-translate-y-0.5"
              >
                See my projects
              </Link>
            </div>
          </div>
          <div className="animate-fade-up stagger-2 relative w-fit mx-auto lg:mx-0" data-testid="hero-portrait-wrapper">
            <div className="absolute -inset-3 rounded-[2rem] bg-amber-200/50 rotate-[-4deg]" aria-hidden="true" />
            <img
              src="/portrait.jpg"
              alt="Portrait of Abhishek Bhattacharjee"
              data-testid="hero-portrait"
              className="relative h-56 w-56 sm:h-72 sm:w-72 lg:h-80 lg:w-80 rounded-[2rem] object-cover shadow-lg ring-1 ring-stone-200"
            />
            <div className="absolute -bottom-4 -left-4 rounded-2xl border border-stone-200 bg-white px-4 py-2 shadow-md" data-testid="hero-portrait-badge">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Based in</p>
              <p className="text-sm font-semibold text-stone-900">Singapore</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="border-y border-stone-200 bg-cream/60">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <div className="flex items-baseline justify-between gap-4">
              <p className="font-mono text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-amber-700" data-testid="testimonial-eyebrow">
                What people say
              </p>
              {testimonials.length > 1 && (
                <p className="hidden sm:block text-xs text-stone-400">Scroll for more →</p>
              )}
            </div>
            <div
              className="mt-8 flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="testimonial-list"
            >
              {testimonials.map((t, i) => (
                <figure
                  key={t.id}
                  className="shrink-0 snap-start w-[85vw] sm:w-[440px] rounded-3xl border border-stone-200 bg-white p-8 shadow-sm"
                  data-testid={i === 0 ? "testimonial-card" : `testimonial-card-${i}`}
                >
                  <Quote className="h-10 w-10 text-amber-500" />
                  <blockquote
                    className="mt-6 font-heading text-lg sm:text-xl font-semibold leading-snug text-stone-900"
                    data-testid={i === 0 ? "testimonial-quote" : `testimonial-quote-${i}`}
                  >
                    "{t.text}"
                  </blockquote>
                  <figcaption className="mt-8 flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 font-heading text-sm font-bold text-amber-800">
                      {initials(t.author)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-stone-900" data-testid={i === 0 ? "testimonial-author" : `testimonial-author-${i}`}>{t.author}</p>
                      {t.source === "topmate" ? (
                        <a
                          href="https://topmate.io/bhattacharjee_abhishek"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={i === 0 ? "testimonial-source-link" : `testimonial-source-link-${i}`}
                          className="text-sm text-stone-500 transition-colors hover:text-amber-700"
                        >
                          {t.rating}★ review via Topmate
                        </a>
                      ) : (
                        <p className="text-sm text-stone-500">{t.rating}★ review</p>
                      )}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
